import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, User, CheckCircle, Truck, Phone } from "@phosphor-icons/react";
import { BUSINESS } from "@/data/site";

export default function CourierLogisticsGuide() {
  return (
    <article data-testid="guide-courier-logistics" className="ji-container py-16 md:py-24 max-w-4xl">
      <nav className="text-sm mb-8">
        <Link to="/" className="text-[var(--ji-muted)] hover:text-[var(--ji-accent)]">Home</Link>
        <span className="mx-2 text-[var(--ji-muted)]">·</span>
        <Link to="/services" className="text-[var(--ji-muted)] hover:text-[var(--ji-accent)]">Services</Link>
        <span className="mx-2 text-[var(--ji-muted)]">·</span>
        <span className="text-[var(--ji-text)]">Courier &amp; Last-Mile Logistics</span>
      </nav>

      <div className="ji-overline mb-4 inline-flex items-center gap-2">
        <Truck size={14} weight="bold" /> Service Guide · Courier Services
      </div>
      <h1
        style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }}
        className="text-4xl md:text-6xl font-bold tracking-tight"
      >
        Courier &amp; Last-Mile<br />
        <span className="text-[var(--ji-accent)] italic">Logistics.</span>
      </h1>

      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[var(--ji-muted)] pb-6 border-b border-[var(--ji-border)]">
        <span className="inline-flex items-center gap-1.5"><User size={14} weight="bold" /> David Joseph · Joseph Industries</span>
        <span className="inline-flex items-center gap-1.5"><Clock size={14} weight="bold" /> 4 min read</span>
        <span>I-10 corridor · New Orleans → Baton Rouge and beyond</span>
      </div>

      <div className="prose prose-lg max-w-none mt-8 space-y-6 text-[var(--ji-text)] leading-relaxed text-base md:text-lg">
        <p>
          &ldquo;Last-mile&rdquo; is the shortest, most expensive, and most failure-prone leg of any delivery. A pallet
          can move from Los Angeles to a warehouse in Kenner in three days without incident &mdash; and then take two
          weeks to reach the medical office six miles away that actually needs it. Last-mile logistics is the discipline
          of closing that gap: making sure the right item, in the right condition, reaches the right hands, on the
          schedule the business actually runs on. Joseph Industries has been running same-day and scheduled courier
          across Southern Louisiana for over two decades, and here&rsquo;s how the modern courier layer works.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          What separates a courier from a delivery service
        </h2>
        <p>
          A parcel carrier is optimized for volume &mdash; thousands of low-value packages per truck, one delivery
          attempt per stop. A courier is optimized for accountability &mdash; a single item, tracked from pickup to
          drop-off, delivered by the same person who signed for it. That distinction matters when the item is a legal
          document that voids a contract if delivered late, a blood sample with a 90-minute cold-chain window, or an
          auto-body part that has an installation crew standing idle at $180/hour until it arrives. Our couriers carry
          their own liability insurance, take signature and photo-confirmation at both ends of the run, and can adjust
          route in real time if the destination changes mid-transit.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Business-to-business is our biggest lane
        </h2>
        <p>
          Roughly three-quarters of our courier work is repeat B2B: law firms shuttling filings between the Orleans and
          Jefferson Parish courthouses, medical supply companies moving orders between clinics, contractors and
          restoration companies picking up parts and materials from suppliers along the I-10 corridor, restaurants
          receiving specialty produce or seafood on tight windows, and shop floors that need a single part for a car
          that&rsquo;s already on the lift. We run these as scheduled routes when the volume is predictable, or as
          on-demand rush runs when it isn&rsquo;t &mdash; typically dispatched within 60 minutes of a call.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Medical, legal, and time-sensitive freight
        </h2>
        <p>
          Medical courier work carries additional discipline: temperature monitoring for biological samples, HIPAA-aware
          handling of paperwork, and STAT windows measured in minutes rather than hours. Legal courier work demands
          verified chain of custody with time-stamped signatures. Time-sensitive freight &mdash; a wedding cake, a
          replacement server drive, a hurricane-response generator part &mdash; needs a courier who understands the cost
          of a missed window and prioritizes accordingly. Every one of our runs has a defined service-level target, and
          we quote transparently based on distance, urgency, and handling requirements.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Coverage and how to book
        </h2>
        <p>
          We cover the full I-10 corridor from New Orleans east to Slidell, west through Metairie, Kenner, LaPlace, and
          Baton Rouge, and south into Houma and Thibodaux. Same-day rush runs are quoted immediately on the phone
          &mdash; give us the pickup address, drop-off address, item description, and required arrival window, and
          we&rsquo;ll dispatch within the hour if the corridor allows. Scheduled routes (daily, weekly, or on a custom
          cadence) are priced on a per-run basis with volume discounts. For businesses running us for the first time,
          we recommend a short trial route to confirm the fit &mdash; there&rsquo;s no long-term commitment, just a
          crew that shows up on time and treats your cargo like it&rsquo;s ours.
        </p>
      </div>

      <div className="mt-16 ji-card p-8 md:p-10" style={{ borderRadius: 4, background: "var(--ji-primary)" }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)] mb-2">Free quote · Same-day or scheduled</div>
            <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight text-white">
              Have a run that needs to happen today?
            </h3>
            <p className="mt-3 text-white/80">
              Business-to-business, medical supply, legal documents, auto parts, restaurant runs &mdash; we quote in
              minutes and dispatch within the hour across the I-10 corridor. Volume discounts on scheduled routes.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="guide-cta-quote"
              className="ji-btn-accent inline-flex items-center justify-center gap-2 h-12 px-5 text-sm font-semibold"
              style={{ borderRadius: 4 }}
            >
              Request a courier <ArrowRight size={14} weight="bold" />
            </Link>
            <a
              href={`tel:${BUSINESS.primaryPhone.tel}`}
              data-testid="guide-cta-call"
              className="ji-btn-outline inline-flex items-center justify-center gap-2 h-12 px-5 text-sm font-semibold bg-white/10 text-white border-white/30 hover:bg-white/20"
              style={{ borderRadius: 4 }}
            >
              <Phone size={14} weight="bold" /> {BUSINESS.primaryPhone.display}
            </a>
          </div>
        </div>
      </div>

      <div className="mt-10 text-sm text-[var(--ji-muted)] flex flex-wrap gap-4">
        <span className="ji-overline">More guides:</span>
        <Link to="/guides/appliance-installation" className="hover:text-[var(--ji-accent)] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Appliance Installation Best Practices</Link>
        <Link to="/guides/home-preservation" className="hover:text-[var(--ji-accent)] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Home Preservation &amp; Asset Protection</Link>
      </div>
    </article>
  );
}
